// Data fields that support image attachments, keyed by dataFieldId so no field is
// identified by its display name. Add an entry to enable a new field (Comment = 3).
export interface ImageUploadFieldConfig {
    key: string;
    labelIdle: string;
    fieldLabel: string;
    maxFiles: number;
    fileCategoryId: number;
}

const buildLabelIdle = (fieldLabel: string, maxFiles: number) =>
    maxFiles > 1
        ? `Click to choose up to ${maxFiles} images for ${fieldLabel}`
        : `Click to choose an image for ${fieldLabel}`;

export const NFPA_CLASSIFICATION_DATA_FIELD_ID = 10010;
export const NORMAL_DAMPER_IMAGE_UPLOAD: ImageUploadFieldConfig = {
    key: "normal-damper-image",
    labelIdle: buildLabelIdle("Normal Damper", 1),
    fieldLabel: "Normal Damper Image",
    maxFiles: 1,
    fileCategoryId: 49,
};

export const ACTIVATED_DAMPER_IMAGE_UPLOAD: ImageUploadFieldConfig = {
    key: "activated-damper-image",
    labelIdle: buildLabelIdle("Activated Damper", 1),
    fieldLabel: "Activated Damper Image",
    maxFiles: 1,
    fileCategoryId: 50,
};

export const IMAGE_UPLOAD_DATAFIELDS: { [dataFieldId: number]: ImageUploadFieldConfig } = {
    3: {
        key: "comment",
        labelIdle: buildLabelIdle("Comment", 4),
        fieldLabel: "Comment",
        maxFiles: 4,
        fileCategoryId: 46,
    },
    10029: {
        key: "deficiency",
        labelIdle: buildLabelIdle("Deficiencies", 4),
        fieldLabel: "Deficiencies",
        maxFiles: 4,
        fileCategoryId:47,
    },
    10030: {
        key: "impairment",
        labelIdle: buildLabelIdle("Impairments", 4),
        fieldLabel: "Impairments",
        maxFiles: 4,
        fileCategoryId: 48,
    },
};
export const DAMPER_IMAGE_UPLOADS: ImageUploadFieldConfig[] = [
    NORMAL_DAMPER_IMAGE_UPLOAD,
    ACTIVATED_DAMPER_IMAGE_UPLOAD,
];

export const getImageUploadConfig = (dataFieldId: number): ImageUploadFieldConfig | undefined =>
    IMAGE_UPLOAD_DATAFIELDS[dataFieldId];

export const hasFieldText = (value: any): boolean =>
    typeof value === "string" ? value.trim().length > 0 : false;

// A category an edit is about to clear: the field losing its value, plus the file category whose
// images therefore become orphaned. Shared by inline edit and multiple edit.
export interface ClearedImageCategory {
    dataFieldId: number;
    fileCategoryId: number;
    fieldLabel: string;
}

// Text-backed categories (Comment, Deficiencies, Impairments) are cleared when their text is
// emptied. Comparing against the previous value avoids warning about an already-empty field.
export const getClearedTextCategory = (
    dataFieldId: number,
    previousValue: any,
    newValue: any
): ClearedImageCategory | undefined => {
    const config = getImageUploadConfig(dataFieldId);
    if (!config) return undefined;
    if (!hasFieldText(previousValue) || hasFieldText(newValue)) return undefined;
    return { dataFieldId, fileCategoryId: config.fileCategoryId, fieldLabel: config.fieldLabel };
};

export const getClearedDamperCategories = (
    previousOption: any,
    newOption: any
): ClearedImageCategory[] => {
    if (!previousOption?.isAttachment) return [];
    if (isSameNfpaOption(previousOption, newOption)) return [];
    return DAMPER_IMAGE_UPLOADS.map((config) => ({
        dataFieldId: NFPA_CLASSIFICATION_DATA_FIELD_ID,
        fileCategoryId: config.fileCategoryId,
        fieldLabel: config.fieldLabel,
    }));
};

const isSameNfpaOption = (previousOption: any, newOption: any): boolean => {
    const previousId = previousOption?.dataFieldLookupId;
    const newId = newOption?.dataFieldLookupId;
    if (previousId === undefined || previousId === null) return false;
    return previousId === newId;
};

export const buildImageDeletionMessage = (
    fieldLabels: string[],
    equipmentCount: number
): string => {
    const uniqueLabels = Array.from(new Set(fieldLabels));
    const fields = uniqueLabels.length === 1
        ? uniqueLabels[0]
        : `${uniqueLabels.slice(0, -1).join(", ")} and ${uniqueLabels[uniqueLabels.length - 1]}`;
    const scope = equipmentCount > 1 ? ` of ${equipmentCount} selected equipment` : "";
    return `${fields} has attached image(s)${scope}. Clearing this will delete the image(s). Do you want to continue?`;
};
